import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectLogInUsername } from "../../../common/authenticateDashboard/authSlice";
import {
  graphUTMOverall,
  SelectVisitors as SelectOverallVisitors,
  SelectPercentBooking as SelectOverallPercentBooking,
  SelectPercentBought as SelectOverallPercentBought,
  SelectLabels as SelectOverallLabels,
  SelectUTMOverallStatus,
} from "../../MarketingPerformance/components/UTMOverall/utmOverallSlice";
import {
  graphUTMCampaign,
  SelectVisitors as SelectCampaignVisitors,
  SelectPercentBooking as SelectCampaignPercentBooking,
  SelectPercentBought as SelectCampaignPercentBought,
  SelectLabels as SelectCampaignLabels,
  SelectUTMCampaignStatus,
} from "../../MarketingPerformance/components/UTMCampaign/utmCampaignSlice";
import {
  graphUTMContent,
  SelectVisitors as SelectContentVisitors,
  SelectPercentBooking as SelectContentPercentBooking,
  SelectPercentBought as SelectContentPercentBought,
  SelectWeeks as SelectContentWeeks,
  SelectLabels as SelectContentLabels,
  SelectUTMContentStatus,
} from "../../MarketingPerformance/components/UTMContent/utmContentSlice";
import {
  graphUTMMedium,
  SelectVisitors as SelectMediumVisitors,
  SelectPercentBooking as SelectMediumPercentBooking,
  SelectPercentBought as SelectMediumPercentBought,
  SelectLabels as SelectMediumLabels,
  SelectUTMMediumStatus,
} from "../../MarketingPerformance/components/UTMMedium/utmMediumSlice";
import {
  graphUTMSource,
  SelectVisitors as SelectSourceVisitors,
  SelectPercentBooking as SelectSourcePercentBooking,
  SelectPercentBought as SelectSourcePercentBought,
  SelectLabels as SelectSourceLabels,
  SelectUTMSourceStatus,
} from "../../MarketingPerformance/components/UTMSource/utmSourceSlice";
import {
  graphUTMTerm,
  SelectVisitors as SelectTermVisitors,
  SelectPercentBooking as SelectTermPercentBooking,
  SelectPercentBought as SelectTermPercentBought,
  SelectLabels as SelectTermLabels,
  SelectUTMTermStatus,
} from "../../MarketingPerformance/components/UTMTerm/utmTermSlice";
import { SingleGraphLoading } from "../../../components/Skeletons";

function TopUsers() {
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectLogInUsername);
  const user = sessionUser?.split("@")[0];
  const hasData = (array) => array && array.length > 0;

  const loadingOverall = useSelector(SelectUTMOverallStatus);
  const overallVisitors = useSelector(SelectOverallVisitors);
  const overallPercentBooking = useSelector(SelectOverallPercentBooking);
  const overallPercentBought = useSelector(SelectOverallPercentBought);
  const overallLabels = useSelector(SelectOverallLabels);

  const loadingCampaign = useSelector(SelectUTMCampaignStatus);
  const CampaignVisitors = useSelector(SelectCampaignVisitors);
  const CampaignPercentBooking = useSelector(SelectCampaignPercentBooking);
  const CampaignPercentBought = useSelector(SelectCampaignPercentBought);
  const CampaignLabels = useSelector(SelectCampaignLabels);

  const loadingContent = useSelector(SelectUTMContentStatus);
  const ContentVisitors = useSelector(SelectContentVisitors);
  const ContentPercentBooking = useSelector(SelectContentPercentBooking);
  const ContentPercentBought = useSelector(SelectContentPercentBought);
  const ContentLabels = useSelector(SelectContentLabels);

  const loadingMedium = useSelector(SelectUTMMediumStatus);
  const MediumVisitors = useSelector(SelectMediumVisitors);
  const MediumPercentBooking = useSelector(SelectMediumPercentBooking);
  const MediumPercentBought = useSelector(SelectMediumPercentBought);
  const MediumLabels = useSelector(SelectMediumLabels);

  const loadingSource = useSelector(SelectUTMSourceStatus);
  const SourceVisitors = useSelector(SelectSourceVisitors);
  const SourcePercentBooking = useSelector(SelectSourcePercentBooking);
  const SourcePercentBought = useSelector(SelectSourcePercentBought);
  const SourceLabels = useSelector(SelectSourceLabels);

  const loadingTerm = useSelector(SelectUTMTermStatus);
  const TermVisitors = useSelector(SelectTermVisitors);
  const TermPercentBooking = useSelector(SelectTermPercentBooking);
  const TermPercentBought = useSelector(SelectTermPercentBought);
  const TermLabels = useSelector(SelectTermLabels);

  useEffect(() => {
    if (!user) return;
    dispatch(graphUTMOverall(user));
    dispatch(graphUTMCampaign(user));
    dispatch(graphUTMContent(user));
    dispatch(graphUTMMedium(user));
    dispatch(graphUTMSource(user));
    dispatch(graphUTMTerm(user));
  }, [user, dispatch]);

  const isLoading = loadingOverall === 'loading' || loadingCampaign === 'loading' || loadingContent === 'loading' || loadingMedium === 'loading' || loadingSource === 'loading' || loadingTerm === 'loading'; 

  const findTopUser = (labels, visitors, percentBooking, percentBought) => {
    let topIndex = 0;
    let maxVisitors = 0;

    visitors.forEach((visitorCount, index) => {
      if (visitorCount > maxVisitors) {
        maxVisitors = visitorCount;
        topIndex = index;
      }
    });

    return {
      trafficSource: labels[topIndex],
      topKeywords: labels[topIndex],
      numberOfUsers: visitors[topIndex],
      percentBooking: percentBooking[topIndex],
      percentSalesDone: percentBought[topIndex]
    };
  };

  const combineTopUTMData = () => {
    let topUTMData = [];

    if (hasData(overallVisitors[0])) {
      topUTMData.push({ ...findTopUser(overallLabels[0], overallVisitors[0], overallPercentBooking[0], overallPercentBought[0]), trafficSource: 'UTM Overall' });
    }

    if (hasData(CampaignVisitors[0])) {
      topUTMData.push({ ...findTopUser(CampaignLabels[0], CampaignVisitors[0], CampaignPercentBooking[0], CampaignPercentBought[0]), trafficSource: 'UTM Campaign' });
    }

    if (hasData(ContentVisitors[0])) {
      topUTMData.push({ ...findTopUser(ContentLabels[0], ContentVisitors[0], ContentPercentBooking[0], ContentPercentBought[0]), trafficSource: 'UTM Content' });
    }

    if (hasData(MediumVisitors[0])) {
      topUTMData.push({ ...findTopUser(MediumLabels[0], MediumVisitors[0], MediumPercentBooking[0], MediumPercentBought[0]), trafficSource: 'UTM Medium' });
    }

    if (hasData(SourceVisitors[0])) {
      topUTMData.push({ ...findTopUser(SourceLabels[0], SourceVisitors[0], SourcePercentBooking[0], SourcePercentBought[0]), trafficSource: 'UTM Source' });
    }

    if (hasData(TermVisitors[0])) {
      topUTMData.push({ ...findTopUser(TermLabels[0], TermVisitors[0], TermPercentBooking[0], TermPercentBought[0]), trafficSource: 'UTM Term' });
    }

    return topUTMData;
  };

  const topUTMData = combineTopUTMData();

  if (isLoading) return <SingleGraphLoading />;

  return (
    <div className="py-8"> 
      <h1 className="text-xl font-semibold text-gray-800 mb-4 mx-auto w-11/12 ms-4"> 
        Top Paid Users Data
      </h1>
      <div className="w-11/12 mx-auto shadow-md mb-5 bg-white rounded-lg ms-4 overflow-x-auto"> 
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3 pl-9 text-lg font-semibold text-gray-800 whitespace-nowrap">Traffic Source</th> 
              <th className="py-3 text-lg font-semibold text-gray-800 whitespace-nowrap">Top Keywords</th>
              <th className="py-3 text-lg font-semibold text-gray-800 whitespace-nowrap">No. of Users</th>
              <th className="py-3 text-lg font-semibold text-gray-800 whitespace-nowrap">% of Booking</th>
              <th className="py-3 text-lg font-semibold text-gray-800 whitespace-nowrap">% of Sales Done</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {topUTMData.map((data, index) => (
              <tr key={index} className="hover:bg-gray-50"> 
                <td className="py-3 pl-10 px-2.5 whitespace-nowrap">{data.trafficSource}</td>
                <td className="py-3 px-2.5 whitespace-nowrap">{data.topKeywords}</td>
                <td className="py-3 px-2.5 whitespace-nowrap">{data.numberOfUsers}</td>
                <td className="py-3 px-2.5 whitespace-nowrap">{`${data.percentBooking}%`}</td>
                <td className="py-3 px-2.5 whitespace-nowrap">{`${data.percentSalesDone}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TopUsers;